import { HttpClient } from "@angular/common/http"
import { Injectable } from "@angular/core"
import { API_HOST } from "environments/environment"

export interface SubscriptionPlan {
    id?: string;
    _id?: string;
    stage: number;
    name: string;
    description: string;
    price: number; // w groszach
    originalPrice?: number | null;
    discountPercent?: number | null;
    durationMonths: number;
    durationYears?: number | null;
    isActive: boolean;
    features: string[];
    invoiceLimit?: number | null;
}

export interface GrantSubscriptionRequest {
    userId: string;
    subscriptionStage: number;
    durationDays: number;
    provider?: string | null;
    paymentType?: 'one_time' | 'recurring';
    isAutoRenewable?: boolean;
    planId?: string | null;
    documentLimit?: number | null; // null = bez limitu (domyślny dla typu), number = customowy limit
}

export interface FeatureComparison {
    _id?: string;
    name: string;
    order: number;
    free: string | boolean;
    start: string | boolean;
    pro: string | boolean;
    business?: string | boolean;
    isAutoGenerated?: boolean; // true dla wiersza "Limit dokumentów"
}

export interface OrderInvoiceData {
    companyName: string;
    nip: string;
    street: string;
    postalCode: string;
    city: string;
    country: string;
}

export interface Order {
    _id?: string;
    id?: string;
    orderNumber: string;
    userId: string;
    planId: string;
    planStage: number;
    planName: string;
    durationMonths: number;
    amount: number; // w groszach
    originalAmount?: number; // cena przed rabatem
    discountPercent?: number;
    paymentMethod: 'przelewy24' | 'payu' | 'transfer';
    status: 'pending' | 'paid' | 'cancelled' | 'refunded';
    invoiceData: OrderInvoiceData;
    // Odbiorca faktury (receiver)
    hasReceiver: boolean;
    receiverData?: OrderInvoiceData;
    // Płatność
    paymentId?: string; // ID transakcji z operatora płatności
    paymentUrl?: string; // URL do bramki płatności
    paidAt?: string;
    // Faktura
    invoiceGenerated?: boolean;
    invoiceId?: string;
    invoiceDocumentNumber?: string; // numer faktury (FVA/1/01/2026)
    invoiceFileName?: string; // pełna nazwa np. "Faktura VAT FVA/1/01/2026"
    invoicePdfUrl?: string; // URL do pobrania faktury PDF
    // KSeF
    ksefNumber?: string; // numer KSeF faktury
    ksefSent?: boolean;  // czy wysłano do KSeF
    // Metadata
    createdAt: string;
    updatedAt?: string;
}

export interface CreateOrderRequest {
    planId: string;
    paymentMethod: 'przelewy24' | 'payu' | 'transfer';
    invoiceData: OrderInvoiceData;
    hasReceiver?: boolean;
    receiverNip?: string;
}

export interface BankTransferDetails {
    recipient: string;
    address?: string;
    iban: string;
    bank?: string;
}

export interface FulfillmentPreview {
    success: boolean;
    error?: string;
    order: {
        orderNumber: string;
        planName: string;
        planStage: number;
        amount: number;
        durationMonths: number;
        paymentMethod: string;
        invoiceData: OrderInvoiceData;
        hasReceiver: boolean;
        receiverData?: OrderInvoiceData;
    };
    plan: {
        name: string;
        stage: number;
        invoiceLimit: number | null;
    };
    currentSubscription: {
        stage: number;
        stageName: string;
        status: string;
        daysRemaining: number;
        validUntil: string | null;
        documentLimit: number | null;
    } | null;
    daysFromPlan: number;
    bonusDays: number;
    totalDays: number;
    isUpgrade: boolean;
    isDowngrade: boolean;
    newDocumentLimit: number | null;
    currentDocumentLimit: number | null;
}

export interface FulfillmentResult {
    success: boolean;
    subscriptionActivated: boolean;
    subscriptionError?: string;
    daysGranted: number;
    bonusDays: number;
    previousPlan: {
        stage: number;
        stageName: string;
        daysRemaining: number;
        documentLimit: number | null;
    } | null;
    newPlan: {
        stage: number;
        stageName: string;
        validUntil: string;
        documentLimit: number | null;
    };
    invoiceGenerated: boolean;
    invoiceError?: string;
    invoiceNumber?: string;
    invoicePdfUrl?: string;
    gusWarning?: string;
    emailSent: boolean;
    emailError?: string;
    notificationSent: boolean;
    notificationError?: string;
    discordSent: boolean;
    discordError?: string;
}

export interface SubscriptionInvoice {
    _id?: string;
    id?: string;
    documentNumber: string; // FVA/1/01/2026
    documentNo?: number;
    documentType: string;
    issueDate: string;
    orderId: string;
    seller: {
        name: string;
        nip: string;
        street: string;
        postalCode: string;
        city: string;
        country: string;
    };
    buyer: {
        name: string;
        nip: string;
        street: string;
        postalCode: string;
        city: string;
        country: string;
    };
    receiver?: {
        name: string;
        nip: string;
        street: string;
        postalCode: string;
        city: string;
        country: string;
    };
    hasReceiver: boolean;
    grossSum: number;
    currency: string;
    isPaid: boolean;
    paidAt?: string;
    pdfUrl?: string;
    pdfGeneratedAt?: string;
    // KSeF
    ksefNumber?: string;
    ksefReferenceNumber?: string;
    ksefSessionReferenceNumber?: string;
    ksefInvoiceNumber?: string;
    ksefOrdinalNumber?: number;
    ksefInvoiceHash?: string;
    ksefReceiveDate?: string;
    ksefInvoicingMode?: string;
    ksefStatus?: {
        code: number;
        description: string;
        details?: any[];
    };
    ksefXml?: string;
    ksefUpo?: string;
    ksefUpoTimestamp?: string;
    ksefSent?: boolean | null;
    ksefSentAt?: string;
    ksefPendingSend?: boolean;
    createdAt: string;
    updatedAt?: string;
}

@Injectable({
    providedIn: 'root'
})
export class SubscriptionsApiService {

    constructor(
        private http: HttpClient
    ) { }

    // === Admin endpoints ===

    /**
     * Pobierz wszystkie plany subskrypcji (admin)
     */
    public getPlans() {
        return this.http.get(API_HOST + 'admin/subscriptions/plans')
    }

    /**
     * Utwórz nowy plan subskrypcji (admin)
     */
    public createPlan(plan: Partial<SubscriptionPlan>) {
        return this.http.post(API_HOST + 'admin/subscriptions/plans', plan)
    }

    /**
     * Zaktualizuj plan subskrypcji (admin)
     */
    public updatePlan(id: string, plan: Partial<SubscriptionPlan>) {
        return this.http.put(API_HOST + `admin/subscriptions/plans/${id}`, { id, ...plan })
    }

    /**
     * Usuń plan subskrypcji (admin)
     */
    public deletePlan(id: string) {
        return this.http.delete(API_HOST + `admin/subscriptions/plans/${id}`)
    }

    /**
     * Pobierz subskrypcję użytkownika (admin)
     */
    public getUserSubscription(userId: string) {
        return this.http.get(API_HOST + `admin/subscriptions/users/${userId}`)
    }

    /**
     * Przyznaj subskrypcję użytkownikowi (admin)
     */
    public grantSubscription(data: GrantSubscriptionRequest) {
        return this.http.post(API_HOST + `admin/subscriptions/users/${data.userId}/grant`, data)
    }

    /**
     * Resetuj licznik dokumentów użytkownika (admin)
     */
    public resetDocumentCounter(userId: string) {
        return this.http.post(API_HOST + `admin/subscriptions/users/${userId}/reset-document-counter`, {})
    }

    /**
     * Ustaw indywidualny limit dokumentów użytkownika (admin)
     */
    public updateDocumentLimit(userId: string, documentLimit: number | null) {
        return this.http.patch(API_HOST + `admin/subscriptions/users/${userId}/document-limit`, { documentLimit })
    }

    /**
     * Pobierz porównanie funkcjonalności (admin)
     */
    public getFeatureComparisons() {
        return this.http.get(API_HOST + 'admin/subscriptions/features')
    }

    /**
     * Zapisz porównanie funkcjonalności (admin) - bulk save
     */
    public saveFeatureComparisons(features: FeatureComparison[]) {
        return this.http.post(API_HOST + 'admin/subscriptions/features', { features })
    }

    // === Public endpoints ===

    /**
     * Pobierz dostępne plany do kupienia (dla użytkowników)
     */
    public getAvailablePlans() {
        return this.http.get(API_HOST + 'subscriptions/plans')
    }

    /**
     * Aktywuj plan FREE (dla użytkownika, bez płatności)
     */
    public activateFreePlan() {
        return this.http.post(API_HOST + 'subscriptions/activate-free', {})
    }

    /**
     * Pobierz porównanie funkcjonalności do wyświetlenia (public)
     */
    public getPublicFeatureComparisons() {
        return this.http.get(API_HOST + 'subscriptions/features')
    }

    /**
     * Pobierz dane do przelewu tradycyjnego (public)
     */
    public getBankTransferDetails() {
        return this.http.get(API_HOST + 'subscriptions/bank-transfer-details')
    }

    // === Orders endpoints ===

    /**
     * Pobierz wszystkie zamówienia (admin)
     */
    public getOrders() {
        return this.http.get(API_HOST + 'admin/subscriptions/orders')
    }

    /**
     * Pobierz szczegóły zamówienia (admin)
     */
    public getOrder(orderId: string) {
        return this.http.get(API_HOST + `admin/subscriptions/orders/${orderId}`)
    }

    /**
     * Aktualizuj status zamówienia (admin)
     * @param orderId ID zamówienia
     * @param status Nowy status
     * @param paidAt Opcjonalna data zapłaty (dla przelewu tradycyjnego)
     * @param options Opcje fulfillmentu (skipInvoice, skipEmail, itd.)
     */
    public updateOrderStatus(orderId: string, status: 'paid' | 'cancelled' | 'refunded', paidAt?: string, options?: {
        skipInvoice?: boolean;
        skipEmail?: boolean;
        skipNotification?: boolean;
        skipDiscord?: boolean;
    }) {
        return this.http.patch(API_HOST + `admin/subscriptions/orders/${orderId}/status`, { status, paidAt, ...options })
    }

    /**
     * Podgląd realizacji zamówienia (admin) — bez wykonywania zmian.
     */
    public getFulfillmentPreview(orderId: string) {
        return this.http.get(API_HOST + `admin/subscriptions/orders/${orderId}/fulfillment-preview`)
    }

    /**
     * Utwórz nowe zamówienie (public - składanie zamówienia przez użytkownika)
     */
    public createOrder(data: CreateOrderRequest) {
        return this.http.post(API_HOST + 'subscriptions/orders', data)
    }

    /**
     * Pobierz zamówienia użytkownika (public)
     */
    public getMyOrders() {
        return this.http.get(API_HOST + 'subscriptions/orders')
    }

    /**
     * Pobierz pojedyncze zamówienie po ID (public)
     */
    public getOrderById(orderId: string) {
        return this.http.get<any>(API_HOST + 'subscriptions/orders/' + orderId)
    }

    /**
     * Anuluj zamówienie użytkownika (tylko status pending)
     */
    public cancelMyOrder(orderId: string) {
        return this.http.post(API_HOST + `subscriptions/orders/${orderId}/cancel`, {})
    }

    // === Subscription Invoices endpoints (admin) ===

    /**
     * Generuj fakturę dla zamówienia (admin)
     */
    public generateInvoice(orderId: string) {
        return this.http.post(API_HOST + `admin/subscriptions/orders/${orderId}/generate-invoice`, {})
    }

    /**
     * Pobierz fakturę dla zamówienia (admin)
     */
    public getOrderInvoice(orderId: string) {
        return this.http.get(API_HOST + `admin/subscriptions/orders/${orderId}/invoice`)
    }

    /**
     * Pobierz wszystkie faktury subskrypcyjne (admin)
     */
    public getSubscriptionInvoices() {
        return this.http.get(API_HOST + 'admin/subscriptions/invoices')
    }

    /**
     * Wyślij fakturę subskrypcyjną natychmiastowo do KSeF (admin)
     */
    public sendInvoiceToKsef(invoiceId: string) {
        return this.http.post(API_HOST + `admin/subscriptions/invoices/${invoiceId}/send-to-ksef`, {})
    }

    /**
     * Pobierz XML faktury z KSeF (manualne pobranie)
     */
    public fetchInvoiceXmlFromKsef(invoiceId: string) {
        return this.http.post(API_HOST + `admin/subscriptions/invoices/${invoiceId}/fetch-xml`, {})
    }

    /**
     * Pobierz UPO faktury z KSeF (manualne pobranie)
     */
    public fetchInvoiceUpoFromKsef(invoiceId: string) {
        return this.http.post(API_HOST + `admin/subscriptions/invoices/${invoiceId}/fetch-upo`, {})
    }
}
